import type { Auth } from 'firebase/auth';
import { signInAnonymously } from 'firebase/auth';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { usePath } from 'crossroad';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Frown } from 'lucide-react';
import { getLocalStorage, setLocalStorage } from './storage';
import { logError } from '../error-logging';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function ForwardTo({ path }: { path: string }) {
    const [, setPath] = usePath();
    setTimeout(() => setPath(path));
    return <></>;
}

export async function requireUser(auth: Auth) {
    await auth.authStateReady();
    if (!auth.currentUser?.uid) {
        return Promise.reject('Not signed in');
    }
    return auth;
}

/**
 * Ensures a user exists. If no user is present, silently signs in anonymously.
 */
export async function ensureUser(auth: Auth) {
    await auth.authStateReady();
    if (auth.currentUser) {
        return auth.currentUser;
    }

    try {
        const credential = await signInAnonymously(auth);
        return credential.user;
    } catch (error) {
        console.error('Silent anonymous sign-in failed:', error);
        throw error;
    }
}

export function LoadingSkeleton() {
    return <>
        <div className="flex flex-col space-y-3 p-5">
            <Skeleton className="h-[125px] w-[250px] rounded-xl" />
            <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
            </div>
        </div>
    </>;
}

export function ShowError({ error, resetErrorBoundary }: { error?: Error; resetErrorBoundary?: () => void }) {
    // Log error to Firebase when component mounts
    if (error) {
        logError(error, {
            page: window.location.pathname,
            component: 'ErrorBoundary',
        });
    }

    return <>
        <div className="p-6 flex gap-3">
            <Frown className="text-red-500" />
            <p className="text-red-500 text-xl">There was an error. Please refresh and try again.</p>
        </div>
        <div className="p-6 -mt-6 flex gap-3">
            <Button variant="outline" onClick={() => resetErrorBoundary ? resetErrorBoundary() : location.reload()}>
                Refresh
            </Button>
        </div>
    </>;
}

export function randomUUID(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for insecure contexts (HTTP)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function generateUniqueId(prefix = '') {
    return `${prefix}${Math.floor(Math.random() * 9999999)}`;
}

export function initializeDarkMode() {
    const settings = JSON.parse(getLocalStorage('settings') || '{}');
    if (settings.dark) {
        document.documentElement.classList.add('dark');
    }
    else {
        document.documentElement.classList.remove('dark');
    }
}

export function toggleDarkMode(dark: boolean) {
    const settings = JSON.parse(getLocalStorage('settings') || '{}');
    setLocalStorage('settings', JSON.stringify({ ...settings, dark }));
    if (dark) {
        document.documentElement.classList.add('dark');
    }
    else {
        document.documentElement.classList.remove('dark');
    }
}

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

export function throttle<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let lastCall = 0;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let lastArgs: Parameters<T> | null = null;

    return (...args: Parameters<T>) => {
        const now = Date.now();
        lastArgs = args;

        const execute = () => {
            lastCall = Date.now();
            timeout = null;
            func(...(lastArgs as Parameters<T>));
        };

        if (now - lastCall >= wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            execute();
        } else if (!timeout) {
            // Trailing edge
            timeout = setTimeout(execute, wait - (now - lastCall));
        }
    };
}